import { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { AiLimitErrorMessage } from '../aiUsage/AiLimitNotice';
import { errorReference } from '../lighting/LightEvaluationCard';
import { getAiLimitErrorKind } from '../../services/aiUsageService';
import { formatRange, type VideoEstimate } from '../../services/flowSurveyService';
import { extractFrames, type ExtractedFrames } from '../../lib/videoFrames';

export interface FlowVideoEstimateProps {
  /** Sends the frames to the AI; resolves with its estimate. */
  estimate: (frames: Blob[], durationSeconds: number) => Promise<VideoEstimate>;
  /** Puts an estimate into the reading form. */
  onResult: (estimate: VideoEstimate) => void;
}

/**
 * The "video" reading method: film the spot for 10–20 s, pick the clip; the
 * browser takes 8 still frames (the clip stays on the device) and the AI
 * estimates the lowest and highest level and whether the direction changes.
 * The result fills the level fields for the user to check before saving.
 */
export function FlowVideoEstimate({ estimate, onResult }: FlowVideoEstimateProps) {
  const { t } = useTranslation();
  const [clip, setClip] = useState<ExtractedFrames | null>(null);
  const [state, setState] = useState<'idle' | 'reading' | 'estimating'>('idle');
  const [readError, setReadError] = useState(false);
  const [aiError, setAiError] = useState<unknown>(null);
  const [result, setResult] = useState<VideoEstimate | null>(null);

  const pick = async (file: File | undefined): Promise<void> => {
    setClip(null);
    setResult(null);
    setAiError(null);
    setReadError(false);
    if (!file) return;
    setState('reading');
    try {
      const frames = await extractFrames(file);
      if (frames.frames.length < 3) throw new Error('too few frames');
      setClip(frames);
    } catch {
      setReadError(true);
    } finally {
      setState('idle');
    }
  };

  const run = async (): Promise<void> => {
    if (!clip) return;
    setState('estimating');
    setAiError(null);
    try {
      const out = await estimate(clip.frames, clip.durationSeconds);
      setResult(out);
      if (out.visible) onResult(out);
    } catch (err) {
      setAiError(err);
    } finally {
      setState('idle');
    }
  };

  return (
    <Stack gap="3" data-testid="flow-video">
      <Text size="sm">{t('flow.video.hint')}</Text>
      <Input
        id="flow-video-file"
        type="file"
        accept="video/*"
        capture="environment"
        label={t('flow.video.file')}
        helperText={t('flow.video.privacy')}
        onChange={(e) => void pick(e.target.files?.[0])}
      />
      {state === 'reading' && <LiveRegion busy><Text color="muted">{t('flow.video.reading')}</Text></LiveRegion>}
      {readError && <Alert compact>{t('flow.video.readError')}</Alert>}
      {clip && (
        <Stack gap="2">
          <Text size="sm" color="secondary">{t('flow.video.frames', { n: clip.frames.length, seconds: Math.round(clip.durationSeconds) })}</Text>
          <Button type="button" variant="secondary" isLoading={state === 'estimating'} onClick={() => void run()}>{t('flow.video.estimate')}</Button>
        </Stack>
      )}
      {state === 'estimating' && <LiveRegion busy><Text color="muted">{t('flow.video.estimating')}</Text></LiveRegion>}
      {aiError !== null &&
        (getAiLimitErrorKind(aiError) ? (
          <AiLimitErrorMessage error={aiError} />
        ) : (
          <Alert compact>
            {t('flow.video.error')}
            {errorReference(aiError) && <Text as="span" size="sm" className="lighting__error-reference">{t('lighting.evaluation.errorReference', { reference: errorReference(aiError) })}</Text>}
          </Alert>
        ))}
      {result && (
        <LiveRegion>
          {result.visible && result.levelMin !== null && result.levelMax !== null ? (
            <Alert tone="info" compact live="off" data-testid="flow-video-result">
              {t('flow.video.result', { range: formatRange(result.levelMin, result.levelMax), confidence: t(`lighting.confidence.${result.confidence}`) })}
              {result.alternating && ` ${String(t('flow.video.alternating'))}`}
              {result.explanation && <Text as="span" size="sm" lang={result.language}>{` ${result.explanation}`}</Text>}
            </Alert>
          ) : (
            <Alert tone="warning" compact live="off">{t('flow.video.notVisible')}</Alert>
          )}
        </LiveRegion>
      )}
    </Stack>
  );
}

export default FlowVideoEstimate;
